import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import emailCampaignService, { CreateCampaignData } from '../../../services/emailCampaignService';
import { sanitizeEmailContent, textToHtml } from '../../../utils/securityUtils';

const EmailComposerPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== 'new';

  const [formData, setFormData] = useState<CreateCampaignData>({
    name: '',
    subject: '',
    content: '',
    targetType: 'ALL_PATRONS',
    tierIds: [],
    scheduledFor: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      loadCampaign(id);
    }
  }, [id, isEdit]);

  const loadCampaign = async (campaignId: string) => {
    try {
      setLoading(true);
      const campaign = await emailCampaignService.getCampaignAnalytics(campaignId);
      setFormData({
        name: campaign.campaign.name,
        subject: campaign.campaign.subject,
        content: campaign.campaign.content,
        targetType: campaign.campaign.targetType,
        tierIds: campaign.campaign.tierIds,
        scheduledFor: campaign.campaign.scheduledFor || '',
      });
    } catch (error) {
      console.error('Failed to load campaign:', error);
      alert('Failed to load campaign');
      navigate('/creator/email-campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.subject || !formData.content) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      if (isEdit && id) {
        await emailCampaignService.updateCampaign(id, formData);
        alert('Campaign updated successfully!');
      } else {
        const campaign = await emailCampaignService.createCampaign(formData);
        alert('Campaign created successfully!');
        navigate(`/creator/email-campaigns/${campaign.id}/edit`);
      }
    } catch (error) {
      console.error('Failed to save campaign:', error);
      alert('Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to send this campaign now?')) return;

    try {
      setSaving(true);
      const result = await emailCampaignService.sendCampaign(id);
      alert(`Campaign sent! ${result.totalSent} emails sent successfully.`);
      navigate('/creator/email-campaigns');
    } catch (error) {
      console.error('Failed to send campaign:', error);
      alert('Failed to send campaign');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const before = text.substring(0, start);
    const after = text.substring(end);

    setFormData({
      ...formData,
      content: before + variable + after,
    });

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/creator/email-campaigns')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-2"
        >
          ← Back to Campaigns
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Edit Campaign' : 'Create Campaign'}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg p-6 space-y-6">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Welcome New Patrons"
                required
              />
            </div>

            {/* Subject Line */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject Line <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Welcome to my Patreon! 🎉"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Keep it short and engaging (50 characters or less recommended)
              </p>
            </div>

            {/* Email Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Content <span className="text-red-500">*</span>
              </label>
              <div className="mb-2 flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => insertVariable('{{patron_name}}')}
                  className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                >
                  + Patron Name
                </button>
                <button
                  type="button"
                  onClick={() => insertVariable('{{tier_name}}')}
                  className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                >
                  + Tier Name
                </button>
                <button
                  type="button"
                  onClick={() => insertVariable('{{creator_name}}')}
                  className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                >
                  + Your Name
                </button>
              </div>
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={12}
                placeholder="Write your email content here... You can use HTML for formatting."
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Supports HTML. Use variables like {'{{patron_name}}'} for personalization.
              </p>
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.targetType}
                onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="ALL_PATRONS">All Patrons (active + former)</option>
                <option value="ACTIVE_ONLY">Active Patrons Only</option>
                <option value="NEW_PATRONS">New Patrons (last 30 days)</option>
                <option value="CHURNED_PATRONS">Churned Patrons (former members)</option>
                <option value="SPECIFIC_TIERS">Specific Tiers</option>
              </select>
            </div>

            {/* Tier Selection (if SPECIFIC_TIERS) */}
            {formData.targetType === 'SPECIFIC_TIERS' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Tiers
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Note: Tier selection UI would connect to your tiers API here
                </p>
                <input
                  type="text"
                  placeholder="Enter tier IDs (comma-separated)"
                  value={formData.tierIds?.join(', ')}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tierIds: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                    })
                  }
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.scheduledFor}
                onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Leave empty to save as draft. Or send immediately after saving.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEdit ? 'Update Campaign' : 'Create Campaign'}
              </button>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleSendNow}
                  disabled={saving}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  Send Now
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Preview */}
          {showPreview && (
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Email Preview</h3>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="mb-3 pb-3 border-b">
                  <div className="text-xs text-gray-500 mb-1">Subject:</div>
                  <div className="font-medium">{formData.subject || 'No subject'}</div>
                </div>
                <div
                  className="text-sm"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeEmailContent(formData.content.replace(/\n/g, '<br />')) || 'No content',
                  }}
                />
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Tips for Great Campaigns</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Keep subject lines under 50 characters</li>
              <li>• Use personalization variables</li>
              <li>• Include a clear call-to-action</li>
              <li>• Test on different devices</li>
              <li>• Send at optimal times (Tue-Thu, 10am-2pm)</li>
            </ul>
          </div>

          {/* Stats (if edit mode) */}
          {isEdit && (
            <div className="bg-white shadow-sm rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Campaign Info</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Status: <span className="font-medium">Draft</span></div>
                <div>Created: <span className="font-medium">Today</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailComposerPage;
